/**
 * RawMaterialNameAutocomplete — iter225 typeahead for the purchase-bill
 * "Product / Material Name" input.
 *
 * Distinct from ProductCombobox (which uses id-based selection) because
 * purchase bills store product_name as a free string. Renders:
 *  1. A text input with matching raw_materials as a dropdown.
 *  2. On PICK or on BLUR (if the typed name exactly matches an RM),
 *     fires an async fetch to GET /api/inventory/raw-materials/last-price
 *     and calls `onLastPrice({ last_rate, invoice_date, vendor_name })`
 *     so the parent can auto-populate the unit_price cell.
 *  3. Renders a tiny caption under the input showing the last-rate hint.
 *
 * Usage:
 *   <RawMaterialNameAutocomplete
 *     value={it.product_name}
 *     materials={materials}             // full raw_materials list
 *     onChange={(name) => update({ product_name: name })}
 *     onLastPrice={(hit) => update({ unit_price: hit.last_rate })}
 *     testId={`line-product-${idx}`}
 *   />
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RawMaterialNameAutocomplete({
  value = '',
  materials = [],
  onChange,
  onLastPrice,
  placeholder = 'Type material…',
  testId,
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [highlight, setHighlight] = useState(0);
  const [hint, setHint] = useState(null); // { last_rate, invoice_date, vendor_name }
  const containerRef = useRef(null);

  // Sync from parent when value changes externally
  useEffect(() => {
    if (!open) setQuery(value || '');
  }, [value, open]);

  const matches = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return materials.slice(0, 50);
    return materials.filter(m => (m.name || '').toLowerCase().includes(q)).slice(0, 50);
  }, [materials, query]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const fetchLastRate = async (name) => {
    if (!name || !name.trim()) return;
    try {
      const res = await axios.get(
        `${API}/inventory/raw-materials/last-price`,
        { params: { name: name.trim() }, withCredentials: true },
      );
      const data = res.data || {};
      if (data.last_rate != null && data.last_rate > 0) {
        setHint({
          last_rate: data.last_rate,
          invoice_date: data.invoice_date,
          vendor_name: data.vendor_name,
        });
        onLastPrice?.(data);
      } else {
        setHint(null);
      }
    } catch { setHint(null); }
  };

  const pick = (m) => {
    setQuery(m.name);
    setOpen(false);
    onChange?.(m.name);
    fetchLastRate(m.name);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, Math.max(0, matches.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[highlight]) pick(matches[highlight]);
      else {
        setOpen(false);
        fetchLastRate(query);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const onBlur = () => {
    // On blur, if the typed value exactly matches an RM by name, still
    // fetch the last rate (in case the user typed a full name without
    // clicking a suggestion).
    const trimmed = (query || '').trim();
    if (!trimmed) return;
    const exact = materials.find(m => (m.name || '').toLowerCase() === trimmed.toLowerCase());
    if (exact || trimmed.length >= 2) fetchLastRate(trimmed);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={e => {
          const v = e.target.value;
          setQuery(v);
          setOpen(true);
          setHighlight(0);
          onChange?.(v);
          if (!v) setHint(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        data-testid={testId}
        className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 focus:outline-none focus:border-amber-500 text-xs"
      />
      {hint && hint.last_rate && (
        <div
          data-testid={testId ? `${testId}-hint` : 'rm-last-rate-hint'}
          className="mt-0.5 text-[9px] text-amber-400/80 truncate"
          title="Autofilled from your most recent purchase of this material">
          Last: ₹{Number(hint.last_rate).toFixed(2)}
          {hint.invoice_date && ` · ${(hint.invoice_date || '').slice(0, 10)}`}
          {hint.vendor_name && ` · ${hint.vendor_name}`}
        </div>
      )}
      {open && matches.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-64 overflow-y-auto"
          data-testid={testId ? `${testId}-options` : undefined}>
          {matches.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              data-row-idx={idx}
              onMouseDown={e => { e.preventDefault(); pick(m); }}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 border-b border-zinc-800/60 last:border-b-0 ${idx === highlight ? 'bg-amber-500/15 text-amber-100' : 'text-zinc-200 hover:bg-zinc-800'}`}>
              <span className="flex-1 truncate">{m.name}</span>
              <span className="text-zinc-500 text-[10px] whitespace-nowrap">
                stk {m.current_stock || 0}{m.unit ? ` ${m.unit}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
